import {
  type ThreadId,
  type ThreadBrowserState,
  type GitActionProgressEvent,
  type OrchestrationEvent,
  type OrchestrationShellStreamItem,
  type OrchestrationThreadStreamItem,
  type ThreadDevServerStateChangedPayload,
  type ServerProviderStatusesUpdatedPayload,
  type TerminalEvent,
  ORCHESTRATION_WS_CHANNELS,
  ORCHESTRATION_WS_METHODS,
  type ComposerQueueChangedPayload,
  type ContextMenuItem,
  type NativeApi,
  ServerConfigUpdatedPayload,
  WS_CHANNELS,
  WS_METHODS,
  type WsWelcomePayload,
} from "@taskin-code/contracts";

import { showConfirmDialogFallback } from "./confirmDialogFallback";
import { showContextMenuFallback } from "./contextMenuFallback";
import { randomUUID } from "./lib/utils";
import { WsTransport, type WsTransportState } from "./wsTransport";
import { useArchiveHookStore } from "./archiveHookStore";
import { installLocalExecBridge } from "./localExecBridge";
import { useProjectScriptRunStore } from "./projectScriptRunStore";

let instance: { api: NativeApi; transport: WsTransport } | null = null;
const welcomeListeners = new Set<(payload: WsWelcomePayload) => void>();
const serverConfigUpdatedListeners = new Set<(payload: ServerConfigUpdatedPayload) => void>();
const serverProviderStatusesUpdatedListeners = new Set<
  (payload: ServerProviderStatusesUpdatedPayload) => void
>();
const gitActionProgressListeners = new Set<(payload: GitActionProgressEvent) => void>();
const terminalEventListeners = new Set<(payload: TerminalEvent) => void>();
const orchestrationDomainEventListeners = new Set<(payload: OrchestrationEvent) => void>();
const orchestrationShellEventListeners = new Set<(payload: OrchestrationShellStreamItem) => void>();
const orchestrationThreadEventListeners = new Set<
  (payload: OrchestrationThreadStreamItem) => void
>();
const devServerStatusListeners = new Set<(payload: ThreadDevServerStateChangedPayload) => void>();
const composerQueueChangedListeners = new Set<(payload: ComposerQueueChangedPayload) => void>();
const fallbackBrowserStateListeners = new Set<(state: ThreadBrowserState) => void>();
const fallbackBrowserStates = new Map<ThreadId, ThreadBrowserState>();

function defaultBrowserState(threadId: ThreadId): ThreadBrowserState {
  return {
    threadId,
    open: false,
    activeTabId: null,
    tabs: [],
    lastError: null,
  };
}

function defaultBrowserTitle(url: string): string {
  if (url === "about:blank") {
    return "New tab";
  }
  try {
    return new URL(url).hostname || url;
  } catch {
    return url;
  }
}

function createFallbackTab(url = "about:blank") {
  return {
    id: randomUUID(),
    url,
    title: defaultBrowserTitle(url),
    status: "live" as const,
    isLoading: false,
    canGoBack: false,
    canGoForward: false,
    faviconUrl: null,
    lastCommittedUrl: url,
    lastError: null,
  };
}

function cloneBrowserState(state: ThreadBrowserState): ThreadBrowserState {
  return {
    ...state,
    tabs: state.tabs.map((tab) => ({ ...tab })),
  };
}

function getFallbackBrowserState(threadId: ThreadId): ThreadBrowserState {
  const existing = fallbackBrowserStates.get(threadId);
  if (existing) {
    return existing;
  }
  const initial = defaultBrowserState(threadId);
  fallbackBrowserStates.set(threadId, initial);
  return initial;
}

function emitFallbackBrowserState(threadId: ThreadId): ThreadBrowserState {
  const state = cloneBrowserState(getFallbackBrowserState(threadId));
  for (const listener of fallbackBrowserStateListeners) {
    listener(state);
  }
  return state;
}

function ensureFallbackBrowserWorkspace(threadId: ThreadId): ThreadBrowserState {
  const state = getFallbackBrowserState(threadId);
  if (state.tabs.length === 0) {
    const tab = createFallbackTab();
    state.tabs = [tab];
    state.activeTabId = tab.id;
  }
  state.open = true;
  return state;
}

function resolveFallbackBrowserTab(state: ThreadBrowserState, tabId?: string) {
  const existing =
    (tabId ? state.tabs.find((tab) => tab.id === tabId) : undefined) ??
    (state.activeTabId ? state.tabs.find((tab) => tab.id === state.activeTabId) : undefined) ??
    state.tabs[0];
  if (existing) {
    return existing;
  }
  const tab = createFallbackTab();
  state.tabs = [tab];
  state.activeTabId = tab.id;
  state.open = true;
  return tab;
}

/**
 * Subscribe to the server welcome message. If a welcome was already received
 * before this call, the listener fires synchronously with the cached payload.
 * This avoids the race between WebSocket connect and React effect registration.
 */
export function onServerWelcome(listener: (payload: WsWelcomePayload) => void): () => void {
  welcomeListeners.add(listener);

  const latestWelcome = instance?.transport.getLatestPush(WS_CHANNELS.serverWelcome)?.data ?? null;
  if (latestWelcome) {
    try {
      listener(latestWelcome);
    } catch {
      // Swallow listener errors
    }
  }

  return () => {
    welcomeListeners.delete(listener);
  };
}

/**
 * Subscribe to server config update events. Replays the latest update for
 * late subscribers to avoid missing config validation feedback.
 */
export function onServerConfigUpdated(
  listener: (payload: ServerConfigUpdatedPayload) => void,
): () => void {
  serverConfigUpdatedListeners.add(listener);

  const latestConfig =
    instance?.transport.getLatestPush(WS_CHANNELS.serverConfigUpdated)?.data ?? null;
  if (latestConfig) {
    try {
      listener(latestConfig);
    } catch {
      // Swallow listener errors
    }
  }

  return () => {
    serverConfigUpdatedListeners.delete(listener);
  };
}

/**
 * Subscribe to provider status updates without forcing a full config reload.
 */
export function onServerProviderStatusesUpdated(
  listener: (payload: ServerProviderStatusesUpdatedPayload) => void,
): () => void {
  serverProviderStatusesUpdatedListeners.add(listener);

  const latestProviderStatuses =
    instance?.transport.getLatestPush(WS_CHANNELS.serverProviderStatusesUpdated)?.data ?? null;
  if (latestProviderStatuses) {
    try {
      listener(latestProviderStatuses);
    } catch {
      // Swallow listener errors
    }
  }

  return () => {
    serverProviderStatusesUpdatedListeners.delete(listener);
  };
}

/**
 * Observe the WebSocket connection state.
 *
 * Returns an unsubscribe function. When the app runs against an injected
 * `window.nativeApi` (Electron with a local backend) there is no WsTransport to
 * watch, so the listener is told the connection is fine and never called again.
 */
export function subscribeToConnectionState(
  listener: (state: WsTransportState) => void,
): () => void {
  if (!instance) {
    if (window.nativeApi) {
      listener("open");
      return () => {};
    }
    createWsNativeApi();
  }
  const transport = instance?.transport;
  if (!transport) {
    listener("open");
    return () => {};
  }
  return transport.onStateChange(listener);
}

/**
 * Subscribe to composer queue change pushes.
 *
 * The queue store consumes this rather than being called into from here: the store already
 * imports the native API to issue its RPCs, so a direct import in the other direction would
 * close a module cycle that only survives on hoisting.
 */
export function onComposerQueueChanged(
  listener: (payload: ComposerQueueChangedPayload) => void,
): () => void {
  composerQueueChangedListeners.add(listener);
  return () => {
    composerQueueChangedListeners.delete(listener);
  };
}

export function createWsNativeApi(): NativeApi {
  if (instance) return instance.api;

  const transport = new WsTransport();

  transport.subscribe(WS_CHANNELS.serverWelcome, (message) => {
    const payload = message.data;
    for (const listener of welcomeListeners) {
      try {
        listener(payload);
      } catch {
        // Swallow listener errors
      }
    }
  });
  transport.subscribe(WS_CHANNELS.serverConfigUpdated, (message) => {
    const payload = message.data;
    for (const listener of serverConfigUpdatedListeners) {
      try {
        listener(payload);
      } catch {
        // Swallow listener errors
      }
    }
  });
  transport.subscribe(WS_CHANNELS.serverProviderStatusesUpdated, (message) => {
    const payload = message.data;
    for (const listener of serverProviderStatusesUpdatedListeners) {
      try {
        listener(payload);
      } catch {
        // Swallow listener errors
      }
    }
  });
  transport.subscribe(WS_CHANNELS.gitActionProgress, (message) => {
    const payload = message.data;
    for (const listener of gitActionProgressListeners) {
      try {
        listener(payload);
      } catch {
        // Swallow listener errors
      }
    }
  });
  transport.subscribe(WS_CHANNELS.terminalEvent, (message) => {
    const payload = message.data;
    for (const listener of terminalEventListeners) {
      try {
        listener(payload);
      } catch {
        // Swallow listener errors
      }
    }
  });
  transport.subscribe(ORCHESTRATION_WS_CHANNELS.domainEvent, (message) => {
    const payload = message.data;
    for (const listener of orchestrationDomainEventListeners) {
      try {
        listener(payload);
      } catch {
        // Swallow listener errors
      }
    }
  });
  transport.subscribe(ORCHESTRATION_WS_CHANNELS.devServerStatusChanged, (message) => {
    const payload = message.data;
    for (const listener of devServerStatusListeners) {
      try {
        listener(payload);
      } catch {
        // Swallow listener errors
      }
    }
  });
  transport.subscribe(ORCHESTRATION_WS_CHANNELS.shellEvent, (message) => {
    const payload = message.data;
    for (const listener of orchestrationShellEventListeners) {
      try {
        listener(payload);
      } catch {
        // Swallow listener errors
      }
    }
  });
  transport.subscribe(ORCHESTRATION_WS_CHANNELS.threadEvent, (message) => {
    const payload = message.data;
    for (const listener of orchestrationThreadEventListeners) {
      try {
        listener(payload);
      } catch {
        // Swallow listener errors
      }
    }
  });
  transport.subscribe(WS_CHANNELS.workspaceArchiveHookStatus, (message) => {
    const payload = message.data;
    try {
      useArchiveHookStore.getState().applyEvent(payload);
    } catch {
      // Swallow listener errors
    }
  });
  transport.subscribe(WS_CHANNELS.workspaceProjectScriptStatus, (message) => {
    const payload = message.data;
    try {
      useProjectScriptRunStore.getState().applyEvent(payload);
    } catch {
      // Swallow listener errors
    }
  });
  transport.subscribe(WS_CHANNELS.composerQueueChanged, (message) => {
    const payload = message.data;
    for (const listener of composerQueueChangedListeners) {
      try {
        listener(payload);
      } catch {
        // Swallow listener errors
      }
    }
  });
  // Local commands: the push channels are subscribed here, but everything they
  // do happens in the desktop main process. In a plain browser this installs
  // listeners that never find a bridge and answer nothing, which is exactly the
  // intended behaviour — the relay then reports "no device".
  installLocalExecBridge(transport);
  const api: NativeApi = {
    dialogs: {
      pickFolder: async () => {
        if (!window.desktopBridge) return null;
        return window.desktopBridge.pickFolder();
      },
      confirm: async (message) => {
        return showConfirmDialogFallback(message);
      },
    },
    terminal: {
      open: (input) => transport.request(WS_METHODS.terminalOpen, input),
      write: (input) => transport.request(WS_METHODS.terminalWrite, input),
      resize: (input) => transport.request(WS_METHODS.terminalResize, input),
      clear: (input) => transport.request(WS_METHODS.terminalClear, input),
      restart: (input) => transport.request(WS_METHODS.terminalRestart, input),
      close: (input) => transport.request(WS_METHODS.terminalClose, input),
      onEvent: (callback) => {
        terminalEventListeners.add(callback);
        return () => {
          terminalEventListeners.delete(callback);
        };
      },
    },
    projects: {
      searchEntries: (input) => transport.request(WS_METHODS.projectsSearchEntries, input),
      listDirectory: (input) => transport.request(WS_METHODS.projectsListDirectory, input),
      writeFile: (input) => transport.request(WS_METHODS.projectsWriteFile, input),
      readFile: (input) => transport.request(WS_METHODS.projectsReadFile, input),
      readConfig: (input) => transport.request(WS_METHODS.projectsReadConfig, input),
    },
    files: {
      preview: (input) => transport.request(WS_METHODS.filesPreview, input),
      previewGlobs: (input) => transport.request(WS_METHODS.filesPreviewGlobs, input),
    },
    composerQueue: {
      list: (input) => transport.request(WS_METHODS.composerQueueList, input),
      enqueue: (input) => transport.request(WS_METHODS.composerQueueEnqueue, input),
      update: (input) => transport.request(WS_METHODS.composerQueueUpdate, input),
      remove: (input) => transport.request(WS_METHODS.composerQueueRemove, input),
      send: (input) => transport.request(WS_METHODS.composerQueueSend, input),
    },
    pullRequests: {
      list: (input) => transport.request(WS_METHODS.pullRequestsList, input),
      detail: (input) => transport.request(WS_METHODS.pullRequestsDetail, input),
      activity: (input) => transport.request(WS_METHODS.pullRequestsActivity, input),
      runAction: (input) => transport.request(WS_METHODS.pullRequestsRunAction, input),
      comment: (input) => transport.request(WS_METHODS.pullRequestsComment, input),
      invalidate: (input) => transport.request(WS_METHODS.pullRequestsInvalidate, input),
    },
    shell: {
      openInEditor: (cwd, editor) =>
        transport.request(WS_METHODS.shellOpenInEditor, { cwd, editor }),
      openExternal: async (url) => {
        if (window.desktopBridge) {
          const opened = await window.desktopBridge.openExternal(url);
          if (!opened) {
            throw new Error("Unable to open link.");
          }
          return;
        }

        // Some mobile browsers can return null here even when the tab opens.
        // Avoid false negatives and let the browser handle popup policy.
        window.open(url, "_blank", "noopener,noreferrer");
      },
      showInFolder: async (path) => {
        if (window.desktopBridge) {
          await window.desktopBridge.showInFolder(path);
        }
        // No-op in browser - this is a desktop-only feature
      },
    },
    git: {
      pull: (input) => transport.request(WS_METHODS.gitPull, input),
      status: (input) => transport.request(WS_METHODS.gitStatus, input),
      projectPullRequests: (input) => transport.request(WS_METHODS.gitProjectPullRequests, input),
      readWorkingTreeDiff: (input) => transport.request(WS_METHODS.gitReadWorkingTreeDiff, input),
      runStackedAction: (input) =>
        transport.request(WS_METHODS.gitRunStackedAction, input, { timeoutMs: null }),
      listBranches: (input) => transport.request(WS_METHODS.gitListBranches, input),
      createWorktree: (input) => transport.request(WS_METHODS.gitCreateWorktree, input),
      createDetachedWorktree: (input) =>
        transport.request(WS_METHODS.gitCreateDetachedWorktree, input),
      removeWorktree: (input) => transport.request(WS_METHODS.gitRemoveWorktree, input),
      createBranch: (input) => transport.request(WS_METHODS.gitCreateBranch, input),
      checkout: (input) => transport.request(WS_METHODS.gitCheckout, input),
      init: (input) => transport.request(WS_METHODS.gitInit, input),
      handoffThread: (input) => transport.request(WS_METHODS.gitHandoffThread, input),
      resolvePullRequest: (input) => transport.request(WS_METHODS.gitResolvePullRequest, input),
      preparePullRequestThread: (input) =>
        transport.request(WS_METHODS.gitPreparePullRequestThread, input),
      onActionProgress: (callback) => {
        gitActionProgressListeners.add(callback);
        return () => {
          gitActionProgressListeners.delete(callback);
        };
      },
    },
    workspace: {
      prepareAgentWorktree: (input) =>
        transport.request(WS_METHODS.workspacePrepareAgentWorktree, input),
      prepareThreadWorktree: (input) =>
        transport.request(WS_METHODS.workspacePrepareThreadWorktree, input),
      ensureAgentThreadTerminal: (input) =>
        transport.request(WS_METHODS.workspaceEnsureAgentThreadTerminal, input),
      runProjectScript: (input) => transport.request(WS_METHODS.workspaceRunProjectScript, input),
    },
    contextMenu: {
      show: async <T extends string>(
        items: readonly ContextMenuItem<T>[],
        position?: { x: number; y: number },
      ): Promise<T | null> => {
        if (window.desktopBridge) {
          return window.desktopBridge.showContextMenu(items, position);
        }
        return showContextMenuFallback(items, position);
      },
    },
    server: {
      getConfig: () => transport.request(WS_METHODS.serverGetConfig),
      refreshProviders: () => transport.request(WS_METHODS.serverRefreshProviders),
      listWorktrees: () => transport.request(WS_METHODS.serverListWorktrees),
      transcribeVoice: (input) => {
        if (window.desktopBridge?.server?.transcribeVoice) {
          return window.desktopBridge.server.transcribeVoice(input);
        }
        return transport.request(WS_METHODS.serverTranscribeVoice, input);
      },
      upsertKeybinding: (input) => transport.request(WS_METHODS.serverUpsertKeybinding, input),
    },
    provider: {
      getComposerCapabilities: (input) =>
        transport.request(WS_METHODS.providerGetComposerCapabilities, input),
      compactThread: (input) => transport.request(WS_METHODS.providerCompactThread, input),
      listCommands: (input) => transport.request(WS_METHODS.providerListCommands, input),
      listSkills: (input) => transport.request(WS_METHODS.providerListSkills, input),
      listPlugins: (input) => transport.request(WS_METHODS.providerListPlugins, input),
      readPlugin: (input) => transport.request(WS_METHODS.providerReadPlugin, input),
      listModels: (input) => transport.request(WS_METHODS.providerListModels, input),
      listAgents: (input) => transport.request(WS_METHODS.providerListAgents, input),
    },
    orchestration: {
      getSnapshot: () => transport.request(ORCHESTRATION_WS_METHODS.getSnapshot),
      dispatchCommand: (command) =>
        transport.request(ORCHESTRATION_WS_METHODS.dispatchCommand, { command }),
      repairState: () => transport.request(ORCHESTRATION_WS_METHODS.repairState),
      getTurnDiff: (input) => transport.request(ORCHESTRATION_WS_METHODS.getTurnDiff, input),
      getFullThreadDiff: (input) =>
        transport.request(ORCHESTRATION_WS_METHODS.getFullThreadDiff, input),
      replayEvents: (fromSequenceExclusive) =>
        transport.request(ORCHESTRATION_WS_METHODS.replayEvents, { fromSequenceExclusive }),
      subscribeShell: () => transport.request<void>(ORCHESTRATION_WS_METHODS.subscribeShell, {}),
      unsubscribeShell: () =>
        transport.request<void>(ORCHESTRATION_WS_METHODS.unsubscribeShell, {}),
      subscribeThread: (input) =>
        transport.request<void>(ORCHESTRATION_WS_METHODS.subscribeThread, input),
      unsubscribeThread: (input) =>
        transport.request<void>(ORCHESTRATION_WS_METHODS.unsubscribeThread, input),
      loadOlderThreadHistory: (input) =>
        transport.request(ORCHESTRATION_WS_METHODS.loadOlderThreadHistory, input),
      replayThreadEvents: (input) =>
        transport.request(ORCHESTRATION_WS_METHODS.replayThreadEvents, input),
      setProjectRepoCredentials: (input) =>
        transport.request(ORCHESTRATION_WS_METHODS.setProjectRepoCredentials, input),
      validateProjectRepoCredentials: (input) =>
        transport.request(ORCHESTRATION_WS_METHODS.validateProjectRepoCredentials, input),
      clearProjectRepoCredentials: (input) =>
        transport.request<void>(ORCHESTRATION_WS_METHODS.clearProjectRepoCredentials, input),
      createProjectFromRepository: (input) =>
        transport.request(ORCHESTRATION_WS_METHODS.createProjectFromRepository, input),
      startThreadDevServer: (input) =>
        transport.request(ORCHESTRATION_WS_METHODS.startThreadDevServer, input),
      stopThreadDevServer: (input) =>
        transport.request(ORCHESTRATION_WS_METHODS.stopThreadDevServer, input),
      updateThreadDevCommand: (input) =>
        transport.request<void>(ORCHESTRATION_WS_METHODS.updateThreadDevCommand, input),
      getThreadDevServer: (input) =>
        transport.request(ORCHESTRATION_WS_METHODS.getThreadDevServer, input),
      setThreadDevServerVisibility: (input) =>
        transport.request(ORCHESTRATION_WS_METHODS.setThreadDevServerVisibility, input),
      getThreadUserState: () => transport.request(ORCHESTRATION_WS_METHODS.getThreadUserState, {}),
      markThreadVisited: (input) =>
        transport.request(ORCHESTRATION_WS_METHODS.markThreadVisited, input),
      listThreadDirectory: (input) =>
        transport.request(ORCHESTRATION_WS_METHODS.listThreadDirectory, input),
      onDevServerStatusChanged: (callback) => {
        devServerStatusListeners.add(callback);
        return () => {
          devServerStatusListeners.delete(callback);
        };
      },
      onDomainEvent: (callback) => {
        orchestrationDomainEventListeners.add(callback);
        return () => {
          orchestrationDomainEventListeners.delete(callback);
        };
      },
      onShellEvent: (callback) => {
        orchestrationShellEventListeners.add(callback);
        return () => {
          orchestrationShellEventListeners.delete(callback);
        };
      },
      onThreadEvent: (callback) => {
        orchestrationThreadEventListeners.add(callback);
        return () => {
          orchestrationThreadEventListeners.delete(callback);
        };
      },
    },
    browser: {
      open: async (input) => {
        if (window.desktopBridge) {
          return window.desktopBridge.browser.open(input);
        }
        const state = ensureFallbackBrowserWorkspace(input.threadId);
        if (input.initialUrl && state.tabs.length > 0) {
          const activeTab = resolveFallbackBrowserTab(state);
          activeTab.url = input.initialUrl;
          activeTab.title = defaultBrowserTitle(input.initialUrl);
          activeTab.lastCommittedUrl = input.initialUrl;
        }
        return emitFallbackBrowserState(input.threadId);
      },
      close: async (input) => {
        if (window.desktopBridge) {
          return window.desktopBridge.browser.close(input);
        }
        const state = getFallbackBrowserState(input.threadId);
        state.open = false;
        state.activeTabId = null;
        state.tabs = [];
        state.lastError = null;
        return emitFallbackBrowserState(input.threadId);
      },
      hide: async (input) => {
        if (window.desktopBridge) {
          await window.desktopBridge.browser.hide(input);
        }
      },
      getState: async (input) => {
        if (window.desktopBridge) {
          return window.desktopBridge.browser.getState(input);
        }
        return cloneBrowserState(getFallbackBrowserState(input.threadId));
      },
      setPanelBounds: async (input) => {
        if (window.desktopBridge) {
          return window.desktopBridge.browser.setPanelBounds(input);
        }
        return cloneBrowserState(getFallbackBrowserState(input.threadId));
      },
      navigate: async (input) => {
        if (window.desktopBridge) {
          return window.desktopBridge.browser.navigate(input);
        }
        const state = ensureFallbackBrowserWorkspace(input.threadId);
        const tab = resolveFallbackBrowserTab(state, input.tabId);
        tab.url = input.url;
        tab.title = defaultBrowserTitle(input.url);
        tab.lastCommittedUrl = input.url;
        tab.lastError = null;
        tab.status = "live";
        state.activeTabId = tab.id;
        return emitFallbackBrowserState(input.threadId);
      },
      reload: async (input) => {
        if (window.desktopBridge) {
          return window.desktopBridge.browser.reload(input);
        }
        return cloneBrowserState(getFallbackBrowserState(input.threadId));
      },
      goBack: async (input) => {
        if (window.desktopBridge) {
          return window.desktopBridge.browser.goBack(input);
        }
        return cloneBrowserState(getFallbackBrowserState(input.threadId));
      },
      goForward: async (input) => {
        if (window.desktopBridge) {
          return window.desktopBridge.browser.goForward(input);
        }
        return cloneBrowserState(getFallbackBrowserState(input.threadId));
      },
      newTab: async (input) => {
        if (window.desktopBridge) {
          return window.desktopBridge.browser.newTab(input);
        }
        const state = ensureFallbackBrowserWorkspace(input.threadId);
        const tab = createFallbackTab(input.url);
        state.tabs = [...state.tabs, tab];
        if (input.activate !== false || !state.activeTabId) {
          state.activeTabId = tab.id;
        }
        return emitFallbackBrowserState(input.threadId);
      },
      closeTab: async (input) => {
        if (window.desktopBridge) {
          return window.desktopBridge.browser.closeTab(input);
        }
        const state = getFallbackBrowserState(input.threadId);
        state.tabs = state.tabs.filter((tab) => tab.id !== input.tabId);
        if (state.tabs.length === 0) {
          state.open = false;
          state.activeTabId = null;
        } else if (!state.tabs.some((tab) => tab.id === state.activeTabId)) {
          state.activeTabId = state.tabs[0]?.id ?? null;
        }
        return emitFallbackBrowserState(input.threadId);
      },
      selectTab: async (input) => {
        if (window.desktopBridge) {
          return window.desktopBridge.browser.selectTab(input);
        }
        const state = ensureFallbackBrowserWorkspace(input.threadId);
        const tab = resolveFallbackBrowserTab(state, input.tabId);
        state.activeTabId = tab.id;
        return emitFallbackBrowserState(input.threadId);
      },
      openDevTools: async (input) => {
        if (window.desktopBridge) {
          await window.desktopBridge.browser.openDevTools(input);
        }
      },
      onState: (callback) => {
        if (window.desktopBridge) {
          return window.desktopBridge.browser.onState(callback);
        }
        fallbackBrowserStateListeners.add(callback);
        return () => {
          fallbackBrowserStateListeners.delete(callback);
        };
      },
    },
  };

  instance = { api, transport };
  return api;
}
